/**
 * Die Seiten des normalen IBN-Ablaufs, auf denen der Assistent erscheint.
 * `import` ist der Mehrfachimport des Projekts (`/projekte/:id/import`).
 */
export type IbnWizardSeite =
  | 'projekt'
  | 'import'
  | 'liste'
  | 'messstelle';


/**
 * Weg nach der Auswahl im Projekt:
 * - neu:       Mehrfachimport → Messstellen → Prüfpunkte
 * - bestehend: vorhandene Inbetriebnahme → Messstellen → Prüfpunkte
 */
export type IbnWizardPfad =
  | 'neu'
  | 'bestehend';


export interface IbnWizardSchritt {

  id: string;

  /** Seite, auf der der Schritt angezeigt wird. */
  seite: IbnWizardSeite;

  titel: string;

  text: string;

  /**
   * Werte von `data-wizard-target`, auf die der Schritt zeigt.
   * Das erste sichtbare Element gewinnt; ohne Ziel erscheint das Popup mittig.
   */
  ziele?: string[];

  /**
   * Seiten mit eigenen internen Schritten (Mehrfachimport): Wert von
   * `data-wizard-bereich`, der sichtbar sein muss, damit der Schritt gilt.
   * Wechselt die Seite ihren Bereich, folgt der Assistent (siehe
   * IbnWizardService.bereichAbgleichen) – vorwärts wie rückwärts.
   */
  bereich?: string;

  /** Fehlt das Ziel (z. B. ohne automatische Erkennung), wird der Schritt übersprungen. */
  optional?: boolean;

  /** `data-wizard-target`-Werte; eines muss sichtbar sein, bevor „Weiter“ möglich ist. */
  bereitWenn?: string[];

  /** Hinweis, solange „Weiter“ gesperrt ist. */
  wartetText?: string;

  /**
   * „Weiter“ klickt das Ziel an (gesperrt, solange es deaktiviert ist) –
   * der Seiten- bzw. Bereichswechsel schaltet dann weiter.
   */
  weiterKlicktZiel?: boolean;

  /** „Weiter“ bleibt gesperrt; der Schritt endet nur durch einen Seiten- oder Bereichswechsel. */
  wartetAufSeitenwechsel?: boolean;

  /** Hier endet der Assistent – „Fertig“ statt „Weiter“, auch wenn danach noch Schritte folgen. */
  endet?: boolean;

  /** Kein „Weiter“-Button – die Auswahl erfolgt auf der Seite selbst. */
  ohneWeiter?: boolean;

  /**
   * Hier wählt der Benutzer den Weg. Welcher es wird, ergibt sich aus der
   * folgenden Navigation (Importseite = neu, Messstellenliste = bestehend).
   */
  verzweigung?: boolean;

  /** Gehört nur zum Weg „neue Inbetriebnahme“ (Mehrfachimport). */
  nurNeu?: boolean;

  /** Nach einem Import führt von hier kein Weg zurück (gilt nur im Weg „neu“). */
  keinZurueck?: boolean;
}


export const IBN_WIZARD_SCHRITTE: IbnWizardSchritt[] = [

  // ── Projekt ──

  {
    id: 'einfuehrung',
    seite: 'projekt',
    titel: 'Willkommen beim IBN-Assistenten',
    text:
      'Der Assistent zeigt dir Schritt für Schritt den Ablauf einer Inbetriebnahme: '
      + 'Listen importieren – auch mehrere auf einmal –, Messstellen öffnen und Prüfpunkte abarbeiten.'
  },
  {
    id: 'ibn-auswaehlen',
    seite: 'projekt',
    ziele: ['projekt-inbetriebnahmen'],
    verzweigung: true,
    ohneWeiter: true,
    wartetAufSeitenwechsel: true,
    wartetText: 'Klicke auf „Listen importieren“ oder auf eine vorhandene Messstellen- oder Verbraucherliste.',
    titel: 'Liste importieren oder öffnen',
    text:
      'Über „Listen importieren“ legst du neue Listen aus Excel-Dateien an – Messstellen-, Verbraucher- '
      + 'und SPS-Listen in einem Durchgang. Mit einer vorhandenen Messstellen- oder Verbraucherliste '
      + 'fährst du direkt mit der Prüfung fort.'
  },

  // ── Mehrfachimport (nur neue Inbetriebnahme) ──
  //
  // Der Import hat eigene Schritte ohne Seitenwechsel. Jeder Bereich endet mit
  // einem Schritt, der auf die Schaltfläche der Seite wartet oder sie klickt –
  // der Bereichswechsel der Seite schaltet den Assistenten dann weiter.
  // Importiert wird ausschließlich über die Seite selbst.

  // Bereich 1: Dateien auswählen

  {
    id: 'dateien',
    seite: 'import',
    bereich: 'dateien',
    nurNeu: true,
    ziele: ['import-dateien'],
    bereitWenn: ['import-dateiliste'],
    wartetText: 'Weiter ist möglich, sobald mindestens eine Datei ausgewählt ist.',
    titel: 'Excel-Dateien auswählen',
    text:
      'Wähle eine oder mehrere Excel-Listen aus oder ziehe sie auf die Seite. '
      + 'Die Dateien werden zunächst nur gelesen – gespeichert wird noch nichts.'
  },
  {
    id: 'listentyp',
    seite: 'import',
    bereich: 'dateien',
    nurNeu: true,
    ziele: ['import-listentyp'],
    optional: true,
    titel: 'Listentyp prüfen',
    text:
      'Der Listentyp wird je Datei automatisch erkannt. Messstellen- und Verbraucherlisten werden zur '
      + 'Inbetriebnahme mit Prüfpunkten, SPS-Listen gehen in die SPS-Prüfung. Stimmt der Typ nicht, '
      + 'korrigierst du ihn hier – über die Dateiliste wechselst du zwischen den Dateien.'
  },
  {
    id: 'zu-vorschauen',
    seite: 'import',
    bereich: 'dateien',
    nurNeu: true,
    ziele: ['import-weiter-vorschau'],
    weiterKlicktZiel: true,
    wartetText: 'Weiter ist möglich, sobald jede Datei erkannt ist und einen Listentyp hat.',
    titel: 'Zu den Vorschauen',
    text:
      'Die Importübersicht zählt die Dateien je Listentyp. „Weiter“ öffnet die Vorschau der ersten Datei.'
  },

  // Bereich 2: Vorschau je Datei

  {
    id: 'vorschau',
    seite: 'import',
    bereich: 'vorschau',
    nurNeu: true,
    ziele: ['import-vorschau-kopf'],
    bereitWenn: ['import-abschluss', 'import-sps-vorschau'],
    wartetText: 'Weiter ist möglich, sobald die Vorschau der Datei geladen ist.',
    titel: 'Vorschau je Datei',
    text:
      'Jede Datei bekommt nacheinander ihre eigene Vorschau. Messstellen- und Verbraucherlisten '
      + 'werden dafür analysiert, SPS-Listen zeigen die erkannten Signale. Gespeichert wird noch nichts.'
  },
  {
    id: 'erkennung',
    seite: 'import',
    bereich: 'vorschau',
    nurNeu: true,
    ziele: ['import-erkennung'],
    optional: true,
    titel: 'Erkannte Struktur prüfen',
    text:
      'Hier stehen Arbeitsblatt, Kopfbereich und Kennzeichnung, mit denen die Liste gelesen wird. '
      + 'Passt etwas nicht, korrigierst du es über „Erkennung bearbeiten“.'
  },
  {
    id: 'anlagenteile',
    seite: 'import',
    bereich: 'vorschau',
    nurNeu: true,
    ziele: ['import-anlagenteile'],
    optional: true,
    titel: 'Anlagenteile prüfen',
    text:
      'Die Messstellen werden Anlagenteilen zugeordnet. Kontrolliere die erkannte Spalte – '
      + 'über „Zuordnungen prüfen“ passt du Klartexte und Regeln an.'
  },
  {
    id: 'kategorien',
    seite: 'import',
    bereich: 'vorschau',
    nurNeu: true,
    ziele: ['import-kategorien'],
    optional: true,
    titel: 'Kategorien prüfen',
    text:
      'Die Kategorie bestimmt später, welche Prüfpunkte eine Messstelle bekommt. '
      + 'Über „Zuordnungen prüfen“ kontrollierst du die vorgeschlagenen Regeln.'
  },
  {
    id: 'spalten',
    seite: 'import',
    bereich: 'vorschau',
    nurNeu: true,
    ziele: ['import-spalten', 'import-vorschau'],
    optional: true,
    titel: 'Spalten anpassen',
    text:
      'Die Vorschau zeigt, wie die Liste nach dem Import aussieht. '
      + 'Über „Spalten anpassen“ legst du fest, welche Excel-Spalten übernommen werden.'
  },
  {
    id: 'zeilen',
    seite: 'import',
    bereich: 'vorschau',
    nurNeu: true,
    ziele: ['import-zeilen', 'import-vorschau'],
    optional: true,
    titel: 'Zeilenauswahl prüfen',
    text:
      'Nicht jede Excel-Zeile ist eine Messstelle. Nach der automatischen Zeilenprüfung '
      + 'kontrollierst du über „Zeilenauswahl bearbeiten“, welche Zeilen importiert werden.'
  },
  {
    id: 'sps-vorschau',
    seite: 'import',
    bereich: 'vorschau',
    nurNeu: true,
    ziele: ['import-sps-vorschau'],
    optional: true,
    titel: 'SPS-Signale prüfen',
    text:
      'Die Zahlen zeigen, wie viele Signale importiert und welche Zeilen übersprungen werden. '
      + 'Darunter blätterst du durch alle Einträge; bei mehreren Tabellenblättern wählst du oben das richtige.'
  },
  {
    id: 'listenname',
    seite: 'import',
    bereich: 'vorschau',
    nurNeu: true,
    ziele: ['import-abschluss', 'import-sps-listenname'],
    optional: true,
    titel: 'Liste benennen',
    text:
      'Prüfe den Namen, unter dem die Liste im Projekt erscheint. '
      + 'Ohne eigenen Namen wird der Dateiname verwendet.'
  },
  {
    id: 'vorschau-bestaetigen',
    seite: 'import',
    bereich: 'vorschau',
    nurNeu: true,
    ziele: ['import-vorschau-weiter'],
    weiterKlicktZiel: true,
    titel: 'Vorschau bestätigen',
    text:
      '„Weiter“ bestätigt die Vorschau dieser Datei und zeigt die nächste. '
      + 'Nach der letzten Datei geht es zur Zusammenfassung.'
  },

  // Bereich 3: Zusammenfassung und Import

  {
    id: 'zusammenfassung',
    seite: 'import',
    bereich: 'abschluss',
    nurNeu: true,
    ziele: ['import-zusammenfassung'],
    titel: 'Zusammenfassung prüfen',
    text:
      'Hier stehen alle Dateien mit Listenname, Typ und Anzahl der Einträge. '
      + 'Über das Augensymbol öffnest du eine Vorschau erneut.'
  },
  {
    id: 'anlagenteil-quelle',
    seite: 'import',
    bereich: 'abschluss',
    nurNeu: true,
    ziele: ['import-anlagenteil-quelle'],
    optional: true,
    titel: 'Anlagenteile der SPS-Listen',
    text:
      'Für SPS-Listen legst du fest, ob die Werte der Spalte „Ort“ bzw. „Path“ als Anlagenteile '
      + 'des Projekts übernommen werden. Vorhandene Anlagenteile werden wiederverwendet.'
  },
  {
    id: 'importieren',
    seite: 'import',
    bereich: 'abschluss',
    nurNeu: true,
    ziele: ['import-starten'],
    wartetAufSeitenwechsel: true,
    wartetText: 'Nach dem erfolgreichen Import geht es automatisch weiter.',
    titel: 'Import starten',
    text:
      'Erst „Import starten“ speichert die Listen – jede Datei einzeln. Schlägt eine fehl, '
      + 'bleiben die anderen erhalten; das Ergebnis steht je Datei in der Zusammenfassung.'
  },

  // Bereich 4: Ergebnis

  {
    id: 'liste-oeffnen',
    seite: 'import',
    bereich: 'ergebnis',
    nurNeu: true,
    ziele: ['import-liste-oeffnen'],
    optional: true,
    weiterKlicktZiel: true,
    keinZurueck: true,
    titel: 'Liste öffnen',
    text:
      'Die Listen sind gespeichert. Öffne eine Messstellen- oder Verbraucherliste, um mit der Prüfung '
      + 'zu beginnen – „Weiter“ öffnet die erste. SPS-Listen prüfst du über „SPS-Prüfung öffnen“.'
  },
  {
    // Nur SPS-Listen importiert: Für sie gibt es keine Messstellenprüfung – hier ist Schluss.
    id: 'sps-pruefung',
    seite: 'import',
    bereich: 'ergebnis',
    nurNeu: true,
    ziele: ['import-sps-pruefung'],
    optional: true,
    endet: true,
    keinZurueck: true,
    titel: 'SPS-Listen prüfen',
    text:
      'Die SPS-Listen sind gespeichert. Ihre Signale prüfst du zentral in der SPS-Prüfung. '
      + 'Der Assistent begleitet die Prüfung von Messstellen- und Verbraucherlisten und endet hier.'
  },

  // ── Messstellenliste ──

  {
    id: 'liste',
    seite: 'liste',
    ziele: ['liste-filter'],
    keinZurueck: true,
    titel: 'Messstellen finden',
    text:
      'Hier findest du alle Messstellen der Inbetriebnahme. Suche und filtere sie '
      + 'nach Kennzeichen, Kategorie, Status oder Anlagenteil.'
  },
  {
    id: 'messstelle-oeffnen',
    seite: 'liste',
    ziele: ['liste-messstelle'],
    weiterKlicktZiel: true,
    titel: 'Messstelle öffnen',
    text:
      'Ein Klick auf eine Messstelle öffnet ihre Detailseite. '
      + 'Wähle eine Messstelle – oder klicke auf „Weiter“, dann wird die erste geöffnet.'
  },

  // ── Messstelle ──

  {
    id: 'kategorie',
    seite: 'messstelle',
    ziele: ['detail-kategorie'],
    titel: 'Kategorie prüfen',
    text:
      'Kontrolliere zuerst die Kategorie. Sie legt fest, welche Prüfpunkte die Messstelle bekommt.'
  },
  {
    id: 'pruefpunkte',
    seite: 'messstelle',
    ziele: ['detail-pruefpunkte'],
    titel: 'Prüfpunkte abarbeiten',
    text:
      'Setze für jeden Prüfpunkt das Ergebnis und ergänze bei Bedarf eine Bemerkung. '
      + 'Bei „nicht bestanden“ ist eine Fehlerbeschreibung Pflicht.'
  },
  {
    id: 'abschluss',
    seite: 'messstelle',
    titel: 'Geschafft!',
    text:
      'Das ist der grundlegende Ablauf einer Inbetriebnahme: Listen importieren oder öffnen, '
      + 'Messstellen finden, Kategorie prüfen und Prüfpunkte abarbeiten. Über den Zurück-Pfeil '
      + 'geht es zur Liste und zur nächsten Messstelle.'
  }
];
